import React from 'react';
import { faEye, faSackDollar } from '@fortawesome/free-solid-svg-icons';
import { NoRecordFound } from '../../../../components/Common/NoRecordFound';
import { UserModel } from '../../../../interfaces/user.interface';
import { convertTimestampToDateTime } from '../../../../helper/helper';
import AdminTableHeader, { ColumnDef } from '../../../../components/Admin/AdminTableHeader';
import AdminActionButton from '../../../../components/Admin/AdminActionButton';

interface StudentsTableProps {
  students: UserModel[];
  onEditCredits: (student: UserModel) => void;
  onViewDetails?: (student: UserModel) => void;
  onClassroomClick?: (classroomId: string) => void;
  onTeacherClick?: (teacherEmail: string) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
}

const StudentsTable: React.FC<StudentsTableProps> = ({ students, onEditCredits, onViewDetails, onClassroomClick, onTeacherClick, sortBy, sortDirection, onSort }) => {
  const columns: ColumnDef[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'loginName', label: 'Login Name' },
    { key: 'email', label: 'Email' },
    { key: 'teacher', label: 'Teacher' },
    { key: 'classroom', label: 'Classroom' },
    { key: 'booksLeft', label: 'Books Left' },
    { key: 'status', label: 'Status' },
    { key: 'lastSession', label: 'Last Session', sortable: true },
    { key: 'created', label: 'Created', sortable: true },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-full border border-gray-300">
        <AdminTableHeader
          columns={columns}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <tbody>
            {students && students.length > 0 ? (
              students.map((student, index) => (
                <tr key={student.id || index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                  {/* Name */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    {onViewDetails ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onViewDetails(student); }}
                        className="text-storygreen underline hover:text-storyblue cursor-pointer"
                        title="View student details"
                      >
                        {student.studentName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—'}
                      </button>
                    ) : (
                      student.studentName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—'
                    )}
                  </td>

                  {/* Login Name */}
                  <td className="py-2 text-left px-4 border-b text-sm font-mono">
                    {student.loginName || '—'}
                  </td>

                  {/* Email */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    <span className={student.generatedEmail ? 'text-gray-400 italic' : ''}>
                      {student.email || '—'}
                    </span>
                    {student.generatedEmail && (
                      <span className="ml-1 text-xs text-gray-400">(auto)</span>
                    )}
                  </td>

                  {/* Teacher */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    {student.teacherEmail ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onTeacherClick?.(student.teacherEmail!); }}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        title="View teacher in Customers"
                      >
                        {student.teacherEmail}
                      </button>
                    ) : '—'}
                  </td>

                  {/* Classroom ID */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    {student.classroomId ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onClassroomClick?.(student.classroomId!); }}
                        className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        title="View classroom students"
                      >
                        {student.classroomId.substring(0, 12)}…
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>

                  {/* Credits (Story Balance) */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    {(() => {
                      const limit = student.booksCreateLimit ?? 0;
                      const used = student.booksCreatedCounter ?? 0;
                      const balance = limit - used;
                      return (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            balance > 0
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                          title={`${used} used / ${limit} limit`}
                        >
                          {balance}
                        </span>
                      );
                    })()}
                  </td>

                  {/* Status */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        student.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {student.status || 'active'}
                    </span>
                  </td>

                  {/* Last Session */}
                  <td className="py-2 text-left px-4 border-b text-sm whitespace-nowrap">
                    {student.lastSessionAt
                      ? convertTimestampToDateTime(student.lastSessionAt as any)
                      : '—'}
                  </td>

                  {/* Created */}
                  <td className="py-2 text-left px-4 border-b text-sm whitespace-nowrap">
                    {student.timestamp
                      ? convertTimestampToDateTime(student.timestamp as any)
                      : '—'}
                  </td>

                  {/* Actions */}
                  <td className="py-2 text-left px-4 border-b text-sm">
                    <div className="flex gap-3">
                      {onViewDetails && (
                        <AdminActionButton
                          icon={faEye}
                          title="View Details"
                          hoverColor="hover:text-teal-600"
                          onClick={() => onViewDetails(student)}
                        />
                      )}
                      <AdminActionButton
                        icon={faSackDollar}
                        title="Topup Book Credits"
                        hoverColor="hover:text-green-600"
                        onClick={() => onEditCredits(student)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <NoRecordFound colSpan={10} />
            )}
        </tbody>
      </table>
    </div>
  );
};

export default StudentsTable;
